<script setup lang="ts">
import { NegotiationState } from '~/apiClient';
import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

// MODEL
const isModalOpen = defineModel<boolean>({ required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt, tn } = useTypedI18n();
const { negotiation, isBuyer, counterPartProposal, accept, acceptIsPending } = useAuctionNegotiation(() => auction);

// METHODS
const closeModal = (): void => {
    isModalOpen.value = false;
};

const onConfirm = async (): Promise<void> => {
    try {
        await accept();
        closeModal();
    } catch {}
};

// WATCHERS
watch(
    () => negotiation.value.state,
    value => isModalOpen.value && value !== NegotiationState.Open && closeModal()
);
</script>

<template>
    <UModal
        v-model:open="isModalOpen"
        :fullscreen="isMobile"
        :title="tt(isBuyer ? 'auctionNegotiation.acceptModal.titleForBuyer' : 'auctionNegotiation.acceptModal.titleForSeller')"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <div class="space-y-6 text-center">
                <div>
                    <p class="text-dimmed text-xs lg:text-sm">
                        {{ tt(isBuyer ? 'auctionNegotiation.sellerRequest' : 'auctionNegotiation.buyerOffer') }}
                    </p>
                    <p class="text-primary-500 text-2xl font-semibold lg:text-3xl">{{ tn(counterPartProposal, 'currency') }}</p>
                    <SaleFees
                        :reference-date="auction.startAt"
                        :is-buyer="isBuyer"
                        :base-amount="counterPartProposal"
                        :assets-count="auction.assets.length"
                        :fee-configuration="auction.userMeta?.feeOverride"
                    />
                </div>

                <div class="flex flex-col items-center gap-1">
                    <UButton
                        size="xl"
                        :label="tt(isBuyer ? 'auctionNegotiation.acceptRequest' : 'auctionNegotiation.acceptOffer')"
                        block
                        :loading="acceptIsPending"
                        @click="onConfirm"
                    />
                    <UButton size="xl" color="neutral" block variant="link" :label="tt('auctionNegotiation.cancel')" @click="closeModal" />
                </div>
            </div>
        </template>
    </UModal>
</template>
